<template>
  <v-sheet>
    <toolbar mode="prediction" title="Generate" view="list" subtitle="Generate predictions for offline applications using compiled datasets"></toolbar>
    <v-divider />
    <v-form method="post" @submit.prevent="onGenerate">
      <v-row>
        <v-col cols="6">
          <v-card-title>Data selection and filtering</v-card-title>
          <v-card-subtitle>Select data sources and apply filters to narrow focus</v-card-subtitle>
          <v-card-text>
            <v-text-field label="Name" v-model="setup.name" clearable />
            <v-autocomplete :items="setup.files" v-model="setup.completion" item-text="name" item-value="uuid" label="Completion data" clearable />
            <v-autocomplete :items="setup.files" v-model="setup.production" item-text="name" item-value="uuid" label="Production data" clearable />
            <v-autocomplete :items="allColumns" v-model="setup.identifier" label="Unique well identifier" clearable />
          </v-card-text>

          <v-divider />
          <v-data-table :items="setup.table.items" :headers="setup.table.headers" disable-sort disable-pagination :items-per-page="-1" hide-default-footer>
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Filter results matching the following criteria</v-toolbar-title>
                <v-spacer />
                <data-filter :dataset="dataset" :columns="setup.columns" :disabled="!setup.completion && !setup.production" @submitted="onAddFilter" />
              </v-toolbar>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-btn icon @click="onRemoveFilter(item)"><v-icon>mdi-close</v-icon></v-btn>
            </template>
          </v-data-table>

          <v-card-text>
            <v-text-field type="number" label="Holdback" v-model="setup.holdback.period" suffix="months" hide-spin-buttons hint="Remove these many months for comparison" />
            <v-text-field type="number" label="Minimum production" v-model="setup.holdback.minimum" suffix="months" hide-spin-buttons hint="Wells should have at least this much production" />
          </v-card-text>

        </v-col>
        <v-col cols="6">
          <v-card-title>Model selection</v-card-title>
          <v-card-subtitle>Select one or more models for comparative analysis</v-card-subtitle>
          <v-card-text>
            <v-autocomplete :items="setup.methods.options" v-model="setup.methods.selected" item-text="name" item-value="id" label="Method(s)" clearable multiple />
            <v-text-field type="number" label="Length of predictions" v-model="setup.predictions" suffix="months" hint="" hide-spin-buttons />
            <v-container>
              <v-row align="center">
                <v-checkbox v-model="setup.decline.enabled" hide-details class="shrink mr-2 mt-0"></v-checkbox>
                <v-text-field type="number" label="Decline individual wells" :disabled="!setup.decline.enabled" v-model="setup.decline.months" suffix="months" hint="Fit and decline individual wells up to specified period" hide-spin-buttons />
              </v-row>
            </v-container>
            <v-container>
              <v-row align="center">
                <v-checkbox v-model="setup.termination.enabled" hide-details class="shrink mr-2 mt-0"></v-checkbox>
                <v-text-field type="number" label="Terminate individual wells" :disabled="!setup.termination.enabled" v-model="setup.termination.rate" suffix="% per annum" hint="Terminate individual wells at specified rate of annual decline" hide-spin-buttons />
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-title>Data columns</v-card-title>
          <v-card-subtitle>Map production data columns for desired outputs</v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col><v-autocomplete v-model="setup.mapping.date" :items="setup.columns.production" label="Production date" clearable hide-details /></v-col>
              <v-col><v-autocomplete v-model="setup.mapping.targets.oil" :items="setup.columns.production" label="Monthly oil production" clearable hide-details /></v-col>
            </v-row>
            <v-row>
              <v-col><v-autocomplete v-model="setup.mapping.targets.gas" :items="setup.columns.production" label="Monthly gas production" clearable hide-details /></v-col>
              <v-col><v-autocomplete v-model="setup.mapping.targets.water" :items="setup.columns.production" label="Monthly water production" clearable hide-details /></v-col>
            </v-row>
          </v-card-text>
        </v-col>
        <v-col cols="6">
          <v-card-title>Feature mapping</v-card-title>
          <v-card-subtitle class="pb-0">Select completion data columns to use as model features</v-card-subtitle>
          <v-card-subtitle class="text--secondary"><v-icon small color="amber">mdi-alert-circle</v-icon> Please note that the quality of predictions depends on available features. If you do not provide any feature mapping, only production data will be used to generate predictions.</v-card-subtitle>
          <v-card-text>
            <v-row v-if="Object.keys(fields).length" class="mb-4">
              <v-col v-for="(k, v, i) in fields" :key="i" cols="4">
                <v-autocomplete
                  v-model="setup.mapping.features[k]"
                  :label="v"
                  :items="setup.columns.completion"
                  hide-details clearable
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-col>

      </v-row>
      <v-divider />
      <v-card-actions>
        <v-btn type="submit" color="success" depressed :disabled="!isValidGenerate">Generate Predictions</v-btn>
        <v-btn type="button" color="" text @click="$router.go(-1)">Cancel</v-btn>
      </v-card-actions>
    </v-form>
  </v-sheet>
</template>

<script>
import VuePlotly from '@statnett/vue-plotly'
import AxButton from "@/components/button"
import DataFilter from "@/components/Apps/Holdback/DataFilter"
import Predictions from "@/api/predictions"
import Files from "@/api/files"
import Methods from "@/api/methods"
import Holdbacks from "@/api/holdback"
export default {
  name: "Holdback",
  props: ["application"],
  components: {
    AxButton,
    VuePlotly,
    DataFilter,
  },
  data() {
    return {
      name: null,
      files: [],
      methods: {
        options: [],
        selected: []
      },
      predictions: 12,
      holdback: {
        period: 12,
        minimum: 1
      },
      decline: {
        enabled: false,
        months: null,
      },
      termination: {
        enabled: false,
        rate: null,
      },
      completion: null,
      production: null,
      identifier: null,
      columns: {
        completion: [],
        production: []
      },
      filters: {
        completion: [],
        production: []
      },
      mapping: {
        date: null,
        targets: {
          oil: null,
          gas: null,
          water: null
        },
        features: {}
      },
      table: {
        items: [],
        headers: [
          { text: 'File', value: 'file' },
          { text: 'Column', value: 'column' },
          { text: 'Operator', value: 'operator' },
          { text: 'Value(s)', value: 'values' },
          { text: '', value: 'action', width: 1, cellClass: 'text-no-wrap' },
        ]
      },
      params: {},
      charts: {
        map: {
          loaded: false,
          data: [],
          layout: {
            autosize: true,
            hovermode: "closest",
            showlegend: false,
            height: 400,
            mapbox: {
              style: null,
              bearing: 0,
              center: {
                lat: 0,
                lon: 0
              },
              pitch: 0,
              zoom: 8
            },
            font: {
              family: "Roboto, sans-serif",
              color: "#808080"
            },
            margin: { l: 0, r: 0, b: 0, t: 0, pad: 0 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)"
          },
          options: {
            mapboxAccessToken: process.env.VUE_APP_MAPBOX_TOKEN,
            displaylogo: false,
            modeBarButtonsToRemove: ["toImage"],
            responsive: true
          }
        },
      },
    }
  },
  computed: {
    tab: {
      set (tab) {
        this.$router.replace({ hash: tab })
      },
      get () {
        return this.$route.hash.replace('#', '')
      }
    },
    dataset() {
      return { 
        completion: this.setup.completion,
        production: this.setup.production
      }
    },
    allColumns() {
      if (!!this.setup.columns.completion && !!this.setup.columns.production) {
        return this.setup.columns.completion.concat(this.setup.columns.production)
      }
      return []
    },
    fields() {
      let methods = this.setup.methods.options.filter(m => this.setup.methods.selected.includes(m.id))
      let fields = methods.map(m => m.fields).filter(f => this.isObject(f))

      let output = {}
      fields.forEach(element => {
        Object.keys(element).forEach(x => {
          if (!(x in output)) {
            output[x] = element[x]
          }
        })
      })
      return this.sort_object(output)
    },
    inputs() {
      return !!this.normal && !!this.holdback && !!this.params
    },
    listPredictions() {
      return this.predictions.map(p => { 
        return { text: p.name, value: p.uuid } 
      })
    },
    isDataSelected() {
      return  !!this.setup.completion &&
              !! this.setup.production &&
              !! this.setup.identifier
    },
    isMethodsSelected() {
      return !this.isEmpty(this.setup.methods.selected)
    },
    isColumnsSpecified() {
      return  !!this.setup.mapping.date &&
              !!(!!this.setup.mapping.targets.oil || !!this.setup.mapping.targets.gas || !!this.setup.mapping.targets.water)
    },
    isValidGenerate() {
      return  this.isDataSelected && this.isMethodsSelected && this.isColumnsSpecified
      return  false
    },
    config() {
      return {
        identifier: this.setup.identifier,
        query: this.setup.table.items,
        holdback: this.setup.holdback,
        predictions: this.setup.predictions,
        decline: this.setup.decline.period,
        termination: this.setup.termination.rate,
        mapping: this.setup.mapping
      }
    }
  },
  watch: {
    "setup.completion"(val) {
      if (val) {
        this.fetchColumns(val, 'completion')
      }
    },
    "setup.production"(val) {
      if (val) {
        this.fetchColumns(val, 'production')
      }
    },
    "setup.decline.enabled"(val) {
      if (val) {
        this.setup.decline.months = 600
      } else {
        this.setup.decline.months = null
        this.setup.termination.enabled = false
      }
    },
    "setup.termination.enabled"(val) {
      if (val) {
        this.setup.decline.enabled = true
        this.setup.termination.rate = 15
      } else {
        this.setup.termination.rate = null
      }
    },
    normal(val) {
      if (val) {
        this.fetchParams(val)
      }
    }
  },
  mounted() {
    // this.fetchPredictions()
    this.fetchFiles()
    this.fetchMethods()
  },
  methods: {
    async fetchFiles() {
      await Files.list().then(response => {
        this.setup.files = response.data
      })
    },
    async fetchMethods() {
      await Methods.list().then(response => {
        this.setup.methods.options = response.data
      })
    },
    async fetchColumns(val, key) {
      await Files.columns(val).then(response => {
        this.setup.columns[key] = response.data
      })
    },
    async fetchPredictions() {
      await Predictions.list().then(response => {
        this.predictions = response.data
      })
    },
    async fetchParams(uuid) {
      await Predictions.retrieve(uuid).then(response => {
        this.params = response.data.mapping
      })
    },
    async fetchMapData() {
      await Holdbacks.get_map_data(this.normal).then(response => {
        this.map = response.data
        this.map[this.params.api] = this.map[this.params.api].map(String)
        this.plotMap()
      })
    },
    async fetchTableData() {
      await Holdbacks.get_frame_data(this.normal).then(response => {
        this.table = response.data
        this.headers = Object.keys(response.data[0]).map(h => { return { text: h, value: h } } )
      })
    },
    loadData() {
      this.fetchMapData()
      this.fetchTableData()
    },
    unloadData() {
      this.map = {}
      this.table = []
      this.normal = null
      this.holdback = null
      this.params = {}
      this.charts.map.data = []
    },
    onAddFilter(filter) {
      this.setup.table.items.push(filter)
    },
    onRemoveFilter(filter) {
      this.setup.table.items = this.setup.table.items.filter(f => f !== filter)
    },
    plotMap() {
      // Populate map
      let obj = {
        type: "scattermapbox",
        text: this.map[this.params.api],
        lat: this.map[this.params.latitude],
        lon: this.map[this.params.longitude],
        mode: "markers",
        // hoverinfo: 'none',
        marker: {
          size: 6,
          reversescale: true,
          autocolorscale: false,
          colorscale: "Portland",
        }
      }
      this.charts.map.data.push(obj)
      // Set map style
      this.charts.map.layout.mapbox.style = this.isDark ? 'mapbox://styles/schajee/cla6nsgx3002315o16d3x006u' : 'mapbox://styles/schajee/cla6np60a001z14mjo36g7dv5'
      // Center align map
      this.charts.map.layout.mapbox.center.lat = this.mean(this.map[this.params.latitude])
      this.charts.map.layout.mapbox.center.lon = this.mean(this.map[this.params.longitude])
    },
    onGenerate() {
      Holdbacks.create({
        name: 'No Name',
        completion: this.setup.files.find(f => f.uuid == this.setup.completion).id,
        production: this.setup.files.find(f => f.uuid == this.setup.production).id,
        methods: this.setup.methods.selected,
        config: this.config,
        user: this.user.id
      }).then(_ => {
        this.notify('Generating holdback')
        this.$router.push({ name: "holdbackList" })
      })
    }
  }
}
</script>